import utilityService from '@/services/utilityService';

export default {
  namespaced: true,
  
  state: {
    siteConfig: null,
    filterOptions: null,
    locations: [],
    testimonials: [],
    loading: false,
    error: null,
    contactFormStatus: null
  },
  
  getters: {
    siteConfig: state => state.siteConfig,
    filterOptions: state => state.filterOptions,
    locations: state => state.locations,
    testimonials: state => state.testimonials,
    isLoading: state => state.loading,
    hasError: state => state.error !== null,
    getErrorMessage: state => state.error,
    contactFormStatus: state => state.contactFormStatus
  },
  
  mutations: {
    SET_SITE_CONFIG(state, config) {
      state.siteConfig = config;
    },
    SET_FILTER_OPTIONS(state, options) {
      state.filterOptions = options;
    },
    SET_LOCATIONS(state, locations) {
      state.locations = locations;
    },
    SET_TESTIMONIALS(state, testimonials) {
      state.testimonials = testimonials;
    },
    SET_CONTACT_FORM_STATUS(state, status) {
      state.contactFormStatus = status;
    },
    SET_LOADING(state, loading) {
      state.loading = loading;
    },
    SET_ERROR(state, error) {
      state.error = error;
    },
    CLEAR_ERROR(state) {
      state.error = null;
    },
    RESET_CONTACT_FORM_STATUS(state) {
      state.contactFormStatus = null;
    }
  },
  
  actions: {
    /**
     * Fetch site configuration
     */
    async fetchSiteConfig({ commit }) {
      commit('SET_LOADING', true);
      commit('CLEAR_ERROR');
      
      try {
        const response = await utilityService.getSiteConfig();
        commit('SET_SITE_CONFIG', response.data);
      } catch (error) {
        commit('SET_ERROR', error.message || 'Failed to fetch site configuration');
        console.error('Error fetching site config:', error);
      } finally {
        commit('SET_LOADING', false);
      }
    },
    
    /**
     * Fetch filter options
     */
    async fetchFilterOptions({ commit }) {
      commit('SET_LOADING', true);
      commit('CLEAR_ERROR');
      
      try {
        const response = await utilityService.getFilterOptions();
        commit('SET_FILTER_OPTIONS', response.data);
      } catch (error) {
        commit('SET_ERROR', error.message || 'Failed to fetch filter options');
        console.error('Error fetching filter options:', error);
      } finally {
        commit('SET_LOADING', false);
      }
    },
    
    /**
     * Fetch locations
     */
    async fetchLocations({ commit }) {
      commit('SET_LOADING', true);
      commit('CLEAR_ERROR');
      
      try {
        const response = await utilityService.getLocations();
        commit('SET_LOCATIONS', response.data.results || response.data);
      } catch (error) {
        commit('SET_ERROR', error.message || 'Failed to fetch locations');
        console.error('Error fetching locations:', error);
      } finally {
        commit('SET_LOADING', false);
      }
    },
    
    /**
     * Fetch testimonials
     */
    async fetchTestimonials({ commit }, params = {}) {
      commit('SET_LOADING', true);
      commit('CLEAR_ERROR');
      
      try {
        const response = await utilityService.getTestimonials(params);
        commit('SET_TESTIMONIALS', response.data.results || response.data);
      } catch (error) {
        commit('SET_ERROR', error.message || 'Failed to fetch testimonials');
        console.error('Error fetching testimonials:', error);
      } finally {
        commit('SET_LOADING', false);
      }
    },
    
    /**
     * Submit contact form
     */
    async submitContactForm({ commit }, formData) {
      commit('SET_LOADING', true);
      commit('CLEAR_ERROR');
      commit('RESET_CONTACT_FORM_STATUS');
      
      try {
        console.log('Submitting contact form with data:', { ...formData, message: formData.message?.substring(0, 20) + '...' });
        
        const response = await utilityService.submitContactForm(formData);
        
        console.log('Contact form submission successful:', response);
        
        // Get success message from response or use default
        const successMessage = response.data?.message || 'Thank you for your message. We will be in touch soon.';
        
        commit('SET_CONTACT_FORM_STATUS', { 
          success: true, 
          message: successMessage
        });
        
        return response;
      } catch (error) {
        console.error('Error submitting contact form:', error);
        
        // Extract detailed error information if available
        let errorMessage = 'Failed to submit contact form. Please try again.';
        
        if (error.response) {
          // The request was made and the server responded with a status code
          // that falls out of the range of 2xx
          if (error.response.data && error.response.data.message) {
            errorMessage = error.response.data.message;
          } else if (error.response.data && error.response.data.detail) {
            errorMessage = error.response.data.detail;
          } else if (error.response.status === 404) {
            errorMessage = 'Contact form submission endpoint not found. The backend service may not be running.';
          } else if (error.response.status === 500) {
            errorMessage = 'Server error while processing your request. Please try again later.';
          }
        } else if (error.request) {
          // The request was made but no response was received
          errorMessage = 'Unable to reach the server. Please check your internet connection and try again.';
        }
        
        commit('SET_ERROR', errorMessage);
        commit('SET_CONTACT_FORM_STATUS', { 
          success: false, 
          message: errorMessage
        });
        
        throw error;
      } finally {
        commit('SET_LOADING', false);
      }
    },
    
    /**
     * Reset contact form status
     */
    resetContactFormStatus({ commit }) {
      commit('RESET_CONTACT_FORM_STATUS');
    }
  }
}; 